import { useMemo, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ArquivoLido, ROTULO_CAMPO, interpretar, lerTexto, parseCsv } from "./importacaoFuncionarios";

interface LinhaResultado {
  linha: number; matricula: string; nome: string; cpf: string;
  acao: "novo" | "atualizar" | "ignorado" | "erro"; idFuncionario: number;
  erros: string[]; alertas: string[];
}
interface Resultado {
  simulacao: boolean; total: number; novos: number; atualizados: number; ignorados: number;
  comErro: number; comAlerta: number; linhas: LinhaResultado[];
}

type Filtro = "todos" | "erro" | "alerta" | "ok";

const ACAO: Record<LinhaResultado["acao"], { texto: string; variante: "default" | "secondary" | "outline" | "destructive" }> = {
  novo: { texto: "Novo", variante: "default" },
  atualizar: { texto: "Atualizar", variante: "secondary" },
  ignorado: { texto: "Ignorado", variante: "outline" },
  erro: { texto: "Erro", variante: "destructive" },
};

interface Props { open: boolean; onOpenChange: (v: boolean) => void; onConcluido: () => void; }

/** Importação de funcionários por planilha: lê o arquivo, mostra a prévia validada e grava. */
export default function ImportarFuncionariosDialog({ open, onOpenChange, onConcluido }: Props) {
  const [arquivo, setArquivo] = useState<ArquivoLido | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState("");
  const [somenteAtivos, setSomenteAtivos] = useState(true);
  const [previa, setPrevia] = useState<Resultado | null>(null);
  const [gravado, setGravado] = useState<Resultado | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>("todos");

  const reiniciar = () => { setArquivo(null); setNomeArquivo(""); setPrevia(null); setGravado(null); setFiltro("todos"); };
  const fechar = (v: boolean) => { if (!v) reiniciar(); onOpenChange(v); };

  const enviar = async (lido: ArquivoLido, simular: boolean, ativos: boolean) => {
    const r = await apiClient.post<{ mensagem: string; resultado: Resultado }>(
      "/api/rh/ponto/funcionarios/importar",
      { Simular: simular, SomenteAtivos: ativos, Linhas: lido.linhas },
    );
    return r.data.resultado;
  };

  const escolher = async (f: File | undefined) => {
    if (!f) return;
    reiniciar();
    setNomeArquivo(f.name);
    setOcupado(true);
    try {
      const lido = interpretar(parseCsv(await lerTexto(f)));
      setArquivo(lido);
      setPrevia(await enviar(lido, true, somenteAtivos));
    } catch (e: any) {
      toast.error(e.message ?? "Não foi possível ler o arquivo.");
    } finally { setOcupado(false); }
  };

  const mudarSomenteAtivos = async (v: boolean) => {
    setSomenteAtivos(v);
    if (!arquivo) return;
    setOcupado(true);
    try { setPrevia(await enviar(arquivo, true, v)); }
    catch (e: any) { toast.error(e.message ?? "Falha ao gerar a prévia."); }
    finally { setOcupado(false); }
  };

  const importar = async () => {
    if (!arquivo) return;
    setOcupado(true);
    try {
      const r = await enviar(arquivo, false, somenteAtivos);
      setGravado(r);
      toast.success(`${r.novos} novo(s) e ${r.atualizados} atualizado(s).`);
      onConcluido();
    } catch (e: any) { toast.error(e.message ?? "Falha ao importar."); }
    finally { setOcupado(false); }
  };

  const atual = gravado ?? previa;
  const linhas = useMemo(() => (atual?.linhas ?? []).filter((l) =>
    filtro === "todos" ? true
      : filtro === "erro" ? l.acao === "erro"
      : filtro === "alerta" ? l.acao !== "erro" && l.acao !== "ignorado" && l.alertas.length > 0
      : l.acao === "novo" || l.acao === "atualizar"), [atual, filtro]);
  const aGravar = previa ? previa.novos + previa.atualizados : 0;

  return (
    <Dialog open={open} onOpenChange={fechar}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Importar funcionários</DialogTitle>
          <DialogDescription>
            Arquivo CSV exportado do sistema de ponto anterior ou planilha com cabeçalho (Nome, Matrícula, CPF…).
            Reimportar atualiza pela matrícula, sem duplicar. Jornada e vínculo de login não são alterados.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label htmlFor="arquivo-funcionarios">Arquivo (.csv)</Label>
            <Input id="arquivo-funcionarios" type="file" accept=".csv,text/csv,text/plain" disabled={ocupado || !!gravado}
              onChange={(e) => { escolher(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch id="somente-ativos" checked={somenteAtivos} disabled={ocupado || !!gravado} onCheckedChange={mudarSomenteAtivos} />
            <Label htmlFor="somente-ativos">Importar só ativos (ignora quem tem data de demissão)</Label>
          </div>
          {ocupado && <Loader2 className="h-5 w-5 animate-spin mb-2" />}
        </div>

        {arquivo && (
          <div className="text-sm text-muted-foreground space-y-1">
            <div><span className="font-medium text-foreground">{nomeArquivo}</span> — {arquivo.preset}, {arquivo.linhas.length} linha(s).</div>
            <div>Campos: {arquivo.campos.map((c) => ROTULO_CAMPO[c]).join(", ")}.</div>
            {!arquivo.campos.includes("cpf") && (
              <div className="text-amber-600">Este arquivo não traz CPF: o CPF já cadastrado é mantido; quem ainda não tem fica com “CPF pendente” e não registra ponto até completar.</div>
            )}
          </div>
        )}

        {atual && (
          <>
            <div className="flex flex-wrap gap-2 text-sm">
              {gravado && <Badge>Importação concluída</Badge>}
              {([
                ["todos", `Todas (${atual.total})`],
                ["ok", `${gravado ? "Gravadas" : "A gravar"} (${atual.novos + atual.atualizados}: ${atual.novos} novas, ${atual.atualizados} atualizações)`],
                ["alerta", `Com alerta (${atual.comAlerta})`],
                ["erro", `Com erro (${atual.comErro})`],
              ] as [Filtro, string][]).map(([f, t]) => (
                <Button key={f} size="sm" variant={filtro === f ? "default" : "outline"} onClick={() => setFiltro(f)}>{t}</Button>
              ))}
              {atual.ignorados > 0 && <span className="self-center text-muted-foreground">{atual.ignorados} desligado(s) ignorado(s)</span>}
            </div>

            <div className="border rounded-lg overflow-auto flex-1 min-h-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Linha</TableHead>
                    <TableHead className="w-24">Matrícula</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead className="w-32">CPF</TableHead>
                    <TableHead className="w-28">Ação</TableHead>
                    <TableHead>Observações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linhas.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhuma linha neste filtro.</TableCell></TableRow>
                  ) : linhas.map((l) => (
                    <TableRow key={l.linha}>
                      <TableCell>{l.linha}</TableCell>
                      <TableCell>{l.matricula || "—"}</TableCell>
                      <TableCell>{l.nome || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{l.cpf || "—"}</TableCell>
                      <TableCell><Badge variant={ACAO[l.acao].variante}>{ACAO[l.acao].texto}</Badge></TableCell>
                      <TableCell className="text-xs">
                        {l.erros.map((m, i) => <div key={`e${i}`} className="text-destructive">{m}</div>)}
                        {l.alertas.map((m, i) => <div key={`a${i}`} className="text-amber-600">{m}</div>)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => fechar(false)}>{gravado ? "Fechar" : "Cancelar"}</Button>
          {!gravado && (
            <Button onClick={importar} disabled={ocupado || aGravar === 0}>
              <FileUp className="h-4 w-4 mr-2" />Importar {aGravar} funcionário(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
